import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ZoomIn, ZoomOut, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, RotateCcw, Repeat, Loader2, AlertTriangle } from 'lucide-react';
import type { FabricCanvas } from '../../types/fabric';
import { SearchableSelect } from '../SearchableSelect';
import { Skeleton } from '../Skeleton';
import { ColorPicker } from './ColorPicker';
import { Slider } from '../Slider';
import { ToolSection } from './shared/ToolSection';
import { getLogicalSize } from '../../lib/canvasObjectHelpers';
import { useDesignStore } from '../../store/designStore';
import { useColorBudget, findOffendingObject, selectOffendingObject } from '../../hooks/useColorBudget';
import { useThinLineCheck } from '../../hooks/useThinLineCheck';
import { ThinLineDetailsModal } from '../canvas/ThinLineDetailsModal';

interface BackgroundToolProps {
  canvas: FabricCanvas | null;
}

const COLOR_CATEGORY = '__color__';
const ZOOM_MIN = 0.2;
const ZOOM_MAX = 5;
const ZOOM_STEP = 0.1;
const MOVE_STEP = 20;

// Self-registers a spin keyframe once, mirroring Skeleton.tsx's shimmer pattern.
if (typeof document !== 'undefined' && !document.getElementById('bg-tool-spin-keyframes')) {
  const style = document.createElement('style');
  style.id = 'bg-tool-spin-keyframes';
  style.textContent = '@keyframes bg-tool-spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(style);
}

function hexToRgba(hex: string, opacityPct: number): string {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const num = parseInt(full, 16) || 0;
  const r = (num >> 16) & 255, g = (num >> 8) & 255, b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${(Math.max(0, Math.min(100, opacityPct)) / 100).toFixed(2)})`;
}

export function BackgroundTool({ canvas }: BackgroundToolProps) {
  const { t } = useTranslation();
  const activeSide = useDesignStore(state => state.sides.find(s => s.sideKey === state.activeSideKey));
  const [selectedCat, setSelectedCat] = useState<string>('');
  const [page, setPage] = useState(1);
  // Accumulated across pages — "Load more" must append, not replace, the grid.
  const [allBackgrounds, setAllBackgrounds] = useState<any[]>([]);
  const [loadedIds, setLoadedIds] = useState<Set<string>>(new Set());

  // Color category state
  const [colorHex, setColorHex] = useState('#ffffff');
  const [colorOpacity, setColorOpacity] = useState(100);
  // Excludes colorHex itself — applyColorBackground (below) actually stores
  // hexToRgba(colorHex, colorOpacity) on canvas.backgroundColor, an rgba(...)
  // STRING, never the bare hex, but useColorBudget's normalizeColor converts
  // both forms to the same comparable "r,g,b" value (alpha ignored — a print
  // color count has no transparency concept), so the plain hex here still
  // correctly matches and excludes the background's own current slot.
  const colorBudget = useColorBudget(activeSide, colorHex);
  // A blocked background pick has no "own object" to exclude (the background
  // isn't a canvas object at all — findOffendingObject only ever walks
  // canvas.getObjects(), which never includes it) — jump straight to
  // whichever real object is holding up the limit, same as TextTool.tsx.
  const handleColorBlocked = () => {
    if (!activeSide || !canvas) return;
    const offending = findOffendingObject(canvas, activeSide);
    if (offending) selectOffendingObject(canvas, offending);
  };

  // Background surfacing lives HERE, inline in this tool's own panel, not in
  // LayerPanel.tsx — same reasoning as colorBudget.isAtLimit above: a
  // background image is never a canvas object (canvas.getObjects() never
  // includes it), so there's no layer row to attach a badge to. Per direct
  // user request ("Include background as well").
  const thinLineCheck = useThinLineCheck(activeSide);
  const bgThinLineFlag = thinLineCheck.flags.find(f => f.isBackground);
  const [showBgThinLineModal, setShowBgThinLineModal] = useState(false);

  // Active image background state — zoom/pan/repeat apply to whichever image was last selected
  const [activeBgUrl, setActiveBgUrl] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [repeat, setRepeat] = useState(false);
  // True while the selected image is being fetched/decoded for the first time —
  // applying it visibly takes a beat, so the UI needs to say so instead of
  // silently doing nothing until it pops in.
  const [bgLoading, setBgLoading] = useState(false);

  const isColorCategory = selectedCat === COLOR_CATEGORY;

  const { data: catData } = useQuery({
    queryKey: ['background-categories'],
    queryFn:  () => fetch('/api/backgrounds/categories').then(r => r.json()),
    staleTime: 300_000,
  });

  const { data: bgData, isFetching: backgroundsLoading } = useQuery({
    queryKey: ['backgrounds', selectedCat, page],
    queryFn:  () =>
      fetch(`/api/backgrounds?categoryId=${selectedCat}&page=${page}`).then(r => r.json()),
    enabled: !!selectedCat && !isColorCategory,
    staleTime: 60_000,
  });

  useEffect(() => { setAllBackgrounds([]); setLoadedIds(new Set()); }, [selectedCat]);

  useEffect(() => {
    if (!bgData?.backgrounds) return;
    setAllBackgrounds(prev => page === 1 ? bgData.backgrounds : [...prev, ...bgData.backgrounds]);
  }, [bgData, page]);

  const markLoaded = (id: string) => setLoadedIds(prev => (prev.has(id) ? prev : new Set(prev).add(id)));

  // Caches the decoded <img> element per URL so zoom/pan/repeat ticks don't each
  // re-fetch and re-decode the image over the network.
  const loadedImgRef = useRef<{ url: string; element: any } | null>(null);
  // Bumped on every effect run so a stale, slow-resolving load can't clobber a
  // newer one that finished first (fabric.Image.fromURL resolution order isn't
  // guaranteed to match call order under rapid clicks).
  const requestIdRef = useRef(0);

  const renderImageBackground = useCallback((fabric: any, element: any, opts: { zoom: number; offsetX: number; offsetY: number; repeat: boolean; sourceUrl: string }) => {
    if (!canvas) return;
    const { width: cW, height: cH } = getLogicalSize(canvas);
    // Stamped directly on the canvas (same "plain custom property on a
    // Fabric object" convention as isTemplateBackground/__uid elsewhere in
    // this codebase) — a background image is never a canvas OBJECT the way
    // a placed clipart/upload is (canvas.getObjects() never includes it, see
    // useColorBudget.ts's own comment on this), so there's no object to read
    // the URL back off of otherwise. Read by useThinLineCheck.ts to know
    // which background image to check.
    (canvas as any).backgroundImageSourceUrl = opts.sourceUrl;
    if (opts.repeat) {
      canvas.setBackgroundImage(null as any, () => {});
      // Tile size at 100% zoom is relative to the canvas (~4 tiles across its
      // shorter side), not the source's native pixel size — a source image
      // can be a few hundred px or, for an SVG render, several thousand;
      // without this, "repeat" on a high-res source just shows one corner of
      // a single giant tile instead of an actual repeating pattern.
      const naturalW = element.naturalWidth || element.width  || 1;
      const naturalH = element.naturalHeight || element.height || 1;
      const targetTileSize = Math.min(cW, cH) / 4;
      const baseScale = targetTileSize / Math.max(naturalW, naturalH);
      const pattern = new fabric.Pattern({
        source: element,
        repeat: 'repeat',
        patternTransform: [baseScale * opts.zoom, 0, 0, baseScale * opts.zoom, opts.offsetX, opts.offsetY],
      });
      canvas.set('backgroundColor', pattern);
      canvas.renderAll();
    } else {
      canvas.set('backgroundColor', '');
      const img = new fabric.Image(element);
      // "Cover" fit (uniform scale, by the larger ratio) instead of independent
      // x/y stretch — a square image on a rectangular design area used to be
      // squashed to fill both axes exactly. Center-anchored so zooming grows
      // from the middle (not the top-left corner) and offset (0,0) is always
      // a perfectly centered crop — what "reset position" should return to.
      const baseScale = Math.max(cW / (img.width ?? cW), cH / (img.height ?? cH));
      img.set({
        scaleX: baseScale * opts.zoom,
        scaleY: baseScale * opts.zoom,
        left: cW / 2 + opts.offsetX,
        top: cH / 2 + opts.offsetY,
        originX: 'center',
        originY: 'center',
      });
      canvas.setBackgroundImage(img, () => canvas.renderAll());
    }
  }, [canvas]);

  // Re-applies live whenever zoom/pan/repeat change on the currently selected background image
  useEffect(() => {
    if (!activeBgUrl) return;
    const requestId = ++requestIdRef.current;
    const opts = { zoom, offsetX, offsetY, repeat, sourceUrl: activeBgUrl };
    const needsFetch = loadedImgRef.current?.url !== activeBgUrl;
    if (needsFetch) setBgLoading(true);

    (async () => {
      // See designStore.ts's own pendingCanvasCommits comment for the real
      // bug this closes — fabric.Image.fromURL below decodes asynchronously
      // before this effect actually applies the background to the canvas;
      // without this guard, a side/tab switch could dispose the canvas out
      // from under this still-pending load. Only guarded while a real fetch
      // is happening (needsFetch) — re-applying an already-loaded image on a
      // zoom/pan/repeat change is synchronous and has nothing to race.
      if (needsFetch) useDesignStore.getState().beginCanvasCommit();
      try {
        const { fabric } = await import('fabric');
        if (needsFetch) {
          await new Promise<void>(resolve => {
            fabric.Image.fromURL(activeBgUrl, (img: any) => {
              loadedImgRef.current = { url: activeBgUrl, element: img._element };
              resolve();
            }, { crossOrigin: 'anonymous' });
          });
        }
        if (requestId !== requestIdRef.current || !loadedImgRef.current) return; // superseded by a newer change
        renderImageBackground(fabric, loadedImgRef.current.element, opts);
        if (requestId === requestIdRef.current) setBgLoading(false);
      } finally {
        if (needsFetch) useDesignStore.getState().endCanvasCommit();
      }
    })();
  }, [activeBgUrl, zoom, offsetX, offsetY, repeat, renderImageBackground]);

  const selectBackgroundImage = (url: string) => {
    setZoom(1); setOffsetX(0); setOffsetY(0); setRepeat(false);
    setActiveBgUrl(url);
  };

  const applyColorBackground = useCallback((hex: string, opacityPct: number) => {
    if (!canvas) return;
    canvas.setBackgroundImage(null as any, () => {});
    (canvas as any).backgroundImageSourceUrl = undefined; // no image behind a flat color — see renderImageBackground's own comment
    canvas.set('backgroundColor', hexToRgba(hex, opacityPct));
    canvas.renderAll();
  }, [canvas]);

  useEffect(() => {
    if (!isColorCategory) return;
    applyColorBackground(colorHex, colorOpacity);
  }, [isColorCategory, colorHex, colorOpacity, applyColorBackground]);

  const clearBackground = () => {
    if (!canvas) return;
    requestIdRef.current++; // invalidate any in-flight zoom/pan/repeat render
    setActiveBgUrl(null);
    setBgLoading(false);
    canvas.setBackgroundImage(null as any, () => {});
    (canvas as any).backgroundImageSourceUrl = undefined;
    canvas.set('backgroundColor', '');
    canvas.renderAll();
  };

  const nudge = (dx: number, dy: number) => {
    setOffsetX(x => x + dx);
    setOffsetY(y => y + dy);
  };

  const categories: any[] = catData?.tree ?? [];
  const categoryOptions = [
    { value: COLOR_CATEGORY, label: t('background.colorCategory') },
    ...categories.map((c: any) => ({ value: c.id, label: c.imageTitle })),
  ];

  return (
    <div data-testid="background-tool" style={{ padding: 12, display: 'flex', flexDirection: 'column' }}>
      <ToolSection title={t('background.sectionCategory')} divider={false}>
        <SearchableSelect
          options={categoryOptions}
          value={selectedCat}
          onChange={value => { setSelectedCat(value); setPage(1); }}
          placeholder={t('background.selectCategory')}
          searchPlaceholder={t('background.searchCategory')}
          noMatchesLabel={t('background.noMatches')}
        />
      </ToolSection>

      {isColorCategory ? (
        <ToolSection title={t('background.sectionColor')}>
          <ColorPicker value={colorHex} onChange={setColorHex} label={t('background.color')}
            isColorAllowed={colorBudget.isColorAllowed}
            usedColors={colorBudget.usedColors}
            isAtLimit={colorBudget.isAtLimit}
            onBlocked={handleColorBlocked}
            budgetLabel={colorBudget.limit != null ? `${colorBudget.usedColors.length} van ${colorBudget.limit} kleuren gebruikt` : undefined} />
          <div>
            <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>
              {t('background.opacity')}: {colorOpacity}%
            </label>
            <Slider min={0} max={100} step={1} value={colorOpacity} onChange={setColorOpacity} />
          </div>
        </ToolSection>
      ) : (
        <>
          <ToolSection title={t('background.sectionBrowse')}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {allBackgrounds.map((bg: any) => (
                <button
                  key={bg.id}
                  onClick={() => selectBackgroundImage(bg.image_path)}
                  style={{
                    position: 'relative', border: activeBgUrl === bg.image_path ? '2px solid var(--primary)' : '1px solid var(--border)',
                    borderRadius: 4, background: 'none', cursor: 'pointer', padding: 4, aspectRatio: '1', overflow: 'hidden',
                  }}
                  title={bg.label ?? ''}
                >
                  {!loadedIds.has(bg.id) && <Skeleton style={{ position: 'absolute', inset: 4 }} />}
                  <img
                    src={bg.thumb_url ?? bg.image_path}
                    alt={bg.label ?? ''}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', visibility: loadedIds.has(bg.id) ? 'visible' : 'hidden' }}
                    loading="lazy"
                    // For presets with no separately-generated thumbnail
                    // (e.g. the SVG presets — the API returns the same
                    // full-res URL for both thumb_url and image_path), this
                    // <img> and fromURL(..., {crossOrigin: 'anonymous'})
                    // below end up requesting the IDENTICAL URL. Two
                    // concurrent requests to the same URL with mismatched
                    // crossorigin modes (this plain <img> had none) race in
                    // Chrome and one gets net::ERR_FAILED — confirmed live
                    // 2026-08-20: clicking either SVG preset never painted
                    // onto the canvas even though the thin-line check's own
                    // fetch of the same URL succeeded, and a bare direct-
                    // fetch/isolated single-request probe confirmed the S3
                    // bucket's CORS headers were never the actual problem.
                    // Matching crossOrigin here keeps both requests on the
                    // same CORS mode so they no longer race incompatibly.
                    crossOrigin="anonymous"
                    onLoad={() => markLoaded(bg.id)}
                    onError={() => markLoaded(bg.id)}
                  />
                  {bgLoading && activeBgUrl === bg.image_path && (
                    <div style={{
                      position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'rgba(255,255,255,0.6)',
                    }}>
                      <Loader2 size={18} style={{ animation: 'bg-tool-spin 0.8s linear infinite', color: 'var(--primary)' }} />
                    </div>
                  )}
                </button>
              ))}
              {backgroundsLoading && page === 1 && allBackgrounds.length === 0 &&
                Array.from({ length: 9 }).map((_, i) => (
                  <div key={`skeleton-${i}`} style={{ border: '1px solid var(--border)', borderRadius: 4, overflow: 'hidden', aspectRatio: '1', padding: 4, boxSizing: 'border-box' }}>
                    <Skeleton />
                  </div>
                ))
              }
            </div>

            {allBackgrounds.length === 0 && !backgroundsLoading && (
              <p style={{ fontSize: 12, color: 'var(--text-muted)', textAlign: 'center', padding: '16px 0' }}>
                {selectedCat ? t('background.empty') : t('background.selectFirst')}
              </p>
            )}

            {bgData?.loadMoreFlag === 1 && (
              <button onClick={() => setPage(p => p + 1)} className="btn btn-secondary" style={{ width: '100%', fontSize: 12 }} disabled={backgroundsLoading}>
                {backgroundsLoading ? t('background.loading') : t('background.loadMore')}
              </button>
            )}
          </ToolSection>

          {activeBgUrl && bgThinLineFlag && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', margin: '0 0 4px',
              background: '#fef3c7', borderRadius: 6, border: '1px solid #fde68a',
            }}>
              <AlertTriangle size={14} style={{ color: '#b45309', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#92400e', flex: 1 }}>{t('thinLine.backgroundWarning')}</span>
              <button
                onClick={() => setShowBgThinLineModal(true)}
                style={{ fontSize: 11, fontWeight: 600, padding: '4px 8px', borderRadius: 4, border: 'none', background: '#f59e0b', color: 'white', cursor: 'pointer', flexShrink: 0 }}
              >
                {t('thinLine.seeDetails')}
              </button>
            </div>
          )}

          {activeBgUrl && (
            <ToolSection title={t('background.sectionPosition')}>
              {bgLoading && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                  <Loader2 size={14} style={{ animation: 'bg-tool-spin 0.8s linear infinite' }} />
                  {t('background.applying')}
                </div>
              )}
              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('background.zoom')}</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    className="btn btn-secondary" style={{ padding: 4 }} disabled={bgLoading}
                    onClick={() => setZoom(z => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)))}
                    title={t('background.zoomOut')}
                  ><ZoomOut size={14} /></button>
                  <span style={{ fontSize: 12, minWidth: 42, textAlign: 'center' }}>{Math.round(zoom * 100)}%</span>
                  <button
                    className="btn btn-secondary" style={{ padding: 4 }} disabled={bgLoading}
                    onClick={() => setZoom(z => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)))}
                    title={t('background.zoomIn')}
                  ><ZoomIn size={14} /></button>
                </div>
              </div>

              <div>
                <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block', marginBottom: 4 }}>{t('background.move')}</label>
                {/* Conventional cross-shaped D-pad — Reset sits in the obvious center slot. */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 28px)', gridTemplateRows: 'repeat(3, 28px)', gap: 4, justifyContent: 'center' }}>
                  <span /><button className="btn btn-secondary" style={{ padding: 0 }} disabled={bgLoading} onClick={() => nudge(0, -MOVE_STEP)} title={t('background.moveUp')}><ArrowUp size={14} /></button><span />
                  <button className="btn btn-secondary" style={{ padding: 0 }} disabled={bgLoading} onClick={() => nudge(-MOVE_STEP, 0)} title={t('background.moveLeft')}><ArrowLeft size={14} /></button>
                  <button className="btn btn-secondary" style={{ padding: 0 }} disabled={bgLoading} onClick={() => { setOffsetX(0); setOffsetY(0); }} title={t('background.resetPosition')}><RotateCcw size={14} /></button>
                  <button className="btn btn-secondary" style={{ padding: 0 }} disabled={bgLoading} onClick={() => nudge(MOVE_STEP, 0)} title={t('background.moveRight')}><ArrowRight size={14} /></button>
                  <span /><button className="btn btn-secondary" style={{ padding: 0 }} disabled={bgLoading} onClick={() => nudge(0, MOVE_STEP)} title={t('background.moveDown')}><ArrowDown size={14} /></button><span />
                </div>
              </div>

              <button
                onClick={() => setRepeat(r => !r)}
                className="btn btn-secondary"
                disabled={bgLoading}
                style={{ width: '100%', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, color: repeat ? 'var(--primary)' : undefined, fontWeight: repeat ? 700 : 400 }}
                title={t('background.repeatHint')}
              >
                <Repeat size={14} /> {t('background.repeat')}
              </button>
            </ToolSection>
          )}
        </>
      )}

      <button onClick={clearBackground} className="btn btn-secondary" style={{ width: '100%', fontSize: 12, marginTop: 16 }}>
        {t('background.clear')}
      </button>

      {showBgThinLineModal && bgThinLineFlag && (
        <ThinLineDetailsModal
          flags={[bgThinLineFlag]}
          onClose={() => setShowBgThinLineModal(false)}
        />
      )}
    </div>
  );
}
